/**
 * Vendor Auth Context + Provider
 *
 * JWT-based authentication for vendor app using Supabase Auth + OpenAPI types.
 * Session recovery pattern with React Query (industry standard).
 * User validation: Only 'supplier' userType allowed (rejects admin/buyer).
 *
 * Uses OpenAPI-generated UserInfoDto directly (100% reliance on Orval hooks).
 * Replaces old localStorage-based SupplierAuthProvider.
 */

import {
  createContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import * as Sentry from "@sentry/react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAuthSessionControllerLogout,
  useAuthSessionControllerGetCurrentUser,
  getAuthSessionControllerGetCurrentUserQueryKey,
  UserInfoDto,
  SupplierInfoDto,
  setAuthToken,
  clearAuthToken,
  AUTH_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  registerAuthErrorCallback,
} from "@semo/api-contracts";
import { isSupplier } from "@semo/shared-utils";
import { useMySupplierInfo } from "../services/mySupplierInfo";
import {
  SESSION_EXPIRED_MESSAGE,
  SESSION_EXPIRED_TOAST_ID,
} from "../../../../utils/sessionExpired";

const ACCESS_TOKEN_KEY = "semo_access_token";

export interface VendorAuthContextType {
  currentUser: UserInfoDto | null;
  supplierInfo: SupplierInfoDto | null;
  isLoading: boolean;
  isSessionLoading: boolean;
  logout: () => Promise<void>;
  setCurrentUser: (user: UserInfoDto | null) => void;
  checkSession: () => void;
}

export const VendorAuthContext = createContext<
  VendorAuthContextType | undefined
>(undefined);
VendorAuthContext.displayName = "VendorAuthContext";

/**
 * Validate UserInfoDto has 'supplier' userType
 */
function isValidSupplierUser(dto: UserInfoDto): boolean {
  if (!isSupplier(dto.userType)) {
    console.warn(
      `🚨 Non-supplier user attempted access - userType: ${dto.userType}`,
    );
    return false;
  }
  return true;
}

/**
 * DashboardAuthProvider Component (formerly VendorAuthProvider)
 *
 * Industry standard JWT/Bearer Token-based session management for the Dashboard.
 * Strictly relies on /auth/me API and React Query cache.
 */
export function DashboardAuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<UserInfoDto | null>(null);
  const [isTokenLoaded, setIsTokenLoaded] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Sentry User Context Setting
  useEffect(() => {
    if (currentUser) {
      Sentry.setUser({
        id: currentUser.userId,
        username: currentUser.name,
        email: currentUser.contactEmail,
        ip_address: "{{auto}}",
        role: currentUser.userType,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [currentUser]);

  // ✅ Initialize Token from LocalStorage
  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      setAuthToken(token);
      setHasToken(true);
    }
    setIsTokenLoaded(true);
  }, []);

  // ✅ Register Auth Error Callback (Force Logout on 401/Refresh Fail)
  useEffect(() => {
    const unsubscribe = registerAuthErrorCallback(() => {
      console.warn("🚨 Auth Error Detected: Forcing Logout (Vendor)");
      setCurrentUser(null);
      setHasToken(false);
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      clearAuthToken();

      queryClient.removeQueries({
        queryKey: getAuthSessionControllerGetCurrentUserQueryKey(),
      });
      queryClient.clear();
      Sentry.setUser(null);

      // 만료를 알리고, 로그인 뒤 보던 화면으로 돌아오게 주소를 넘긴다(A3-15·A3-28).
      toast.error(SESSION_EXPIRED_MESSAGE, { id: SESSION_EXPIRED_TOAST_ID });
      navigate("/login", {
        replace: true,
        state: { from: `${location.pathname}${location.search}` },
      });
    });
    return unsubscribe;
  }, [queryClient, navigate, location.pathname, location.search]);

  // ✅ Session recovery via /auth/me (Bearer Token)
  const {
    data: authResponse,
    isLoading: isSessionLoading,
    refetch,
  } = useAuthSessionControllerGetCurrentUser({
    query: {
      enabled: isTokenLoaded && hasToken,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    },
  });

  // 세션 응답에서 바로 사용자를 얻는다. effect 로만 옮기면 응답이 온 첫 렌더에 사용자가 비어
  // 있어 보호 경로가 /login 으로 보내 버렸다 — 주소로 바로 들어온 화면이 첫 화면으로 바뀌던
  // 원인(A3-28).
  const sessionUser = useMemo(() => {
    const userData = authResponse?.data;
    return userData && isValidSupplierUser(userData) ? userData : null;
  }, [authResponse]);

  // Update local state when API response arrives
  useEffect(() => {
    setCurrentUser(sessionUser);
  }, [sessionUser]);

  /**
   * Centralized Logout Handler
   */
  const handleLogoutSuccess = useCallback(() => {
    // 1. Clear Local State
    setCurrentUser(null);
    setHasToken(false);

    // 2. Clear Storage & Headers
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    clearAuthToken();

    // 3. Clear React Query Cache (CRITICAL: prevents auto-restoration)
    queryClient.removeQueries({
      queryKey: getAuthSessionControllerGetCurrentUserQueryKey(),
    });
    queryClient.clear();
    Sentry.setUser(null);

    // 4. UI Feedback & Redirect
    toast.success("로그아웃 되었습니다.");
    navigate("/login", { replace: true });
  }, [navigate, queryClient]);

  const logoutMutation = useAuthSessionControllerLogout({
    mutation: {
      onSuccess: () => handleLogoutSuccess(),
      onError: (error) => {
        console.error("Logout API failed:", error);
        handleLogoutSuccess(); // Force logout even on error
      },
    },
  });

  const logout = useCallback(async (): Promise<void> => {
    try {
      await logoutMutation.mutateAsync();
    } catch {
      handleLogoutSuccess();
    }
  }, [logoutMutation, handleLogoutSuccess]);

  const checkSession = useCallback(() => {
    refetch();
  }, [refetch]);

  // 견적서 «공급자» 칸에 쓸 회사 정보 — 임베드 경로는 토큰 교환 응답에 실려 오지만 대시보드는
  // 따로 읽어야 한다. 비워 두면 견적서 작성 창의 공급자 칸이 전부 «알 수 없음»(V-05·A3-12).
  const { data: mySupplierInfo } = useMySupplierInfo({
    enabled: !!(currentUser ?? sessionUser),
    userId: (currentUser ?? sessionUser)?.id,
  });

  const effectiveUser = currentUser ?? sessionUser;

  const value: VendorAuthContextType = {
    currentUser: effectiveUser,
    supplierInfo: effectiveUser ? (mySupplierInfo ?? null) : null,
    isLoading: !isTokenLoaded || (hasToken ? isSessionLoading : false),
    isSessionLoading,
    logout,
    setCurrentUser,
    checkSession,
  };

  return (
    <VendorAuthContext.Provider value={value}>
      {children}
    </VendorAuthContext.Provider>
  );
}
